import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Repeat2, Save, Plus, X, RotateCcw } from "lucide-react";
import { colorForShift, shiftLabel } from "@/lib/shifts";
import { toast } from "sonner";

const DAY_LABELS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

export default function MasterScheduleEditor({ codes, payPeriodId, payPeriodLabel, facilityId }) {
  const [defaultDays, setDefaultDays] = useState(Array.from({ length: 14 }, () => []));
  const [overrideDays, setOverrideDays] = useState(null);
  const [tab, setTab] = useState("default");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!facilityId) return;
    const [defRes, ovrRes] = await Promise.all([
      api.get(`/master-template?facility_id=${facilityId}`),
      payPeriodId ? api.get(`/master-template?facility_id=${facilityId}&pay_period_id=${payPeriodId}`) : Promise.resolve({ data: null }),
    ]);
    setDefaultDays(defRes.data.days || Array.from({ length: 14 }, () => []));
    if (ovrRes.data && ovrRes.data.source === "override") setOverrideDays(ovrRes.data.days || []);
    else setOverrideDays(null);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [payPeriodId, facilityId]);

  const workCodes = codes.filter(c => c.type === "work");

  const activeDays = tab === "override" ? (overrideDays || defaultDays) : defaultDays;
  const setActive = (updater) => {
    if (tab === "override") setOverrideDays(prev => (updater(prev || defaultDays)));
    else setDefaultDays(updater);
  };

  const addTo = (i, code) => setActive(d => d.map((row, idx) => idx === i ? [...row, code] : row));
  const removeAt = (i, j) => setActive(d => d.map((row, idx) => idx === i ? row.filter((_, k) => k !== j) : row));

  const save = async () => {
    setLoading(true);
    try {
      if (tab === "override") {
        await api.put(`/master-template?facility_id=${facilityId}&pay_period_id=${payPeriodId}`, { days: overrideDays || defaultDays });
        toast.success("Override saved for this pay period");
      } else {
        await api.put(`/master-template?facility_id=${facilityId}`, { days: defaultDays });
        toast.success("Default master saved");
      }
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save");
    }
    setLoading(false);
  };

  const startOverride = () => {
    setOverrideDays(defaultDays.map(day => [...day]));
    setTab("override");
  };

  const clearOverride = async () => {
    if (!window.confirm("Remove the override and use the default master for this pay period?")) return;
    try {
      await api.delete(`/master-template/override/${payPeriodId}?facility_id=${facilityId}`);
      setOverrideDays(null);
      setTab("default");
      toast.success("Override cleared");
    } catch { toast.error("Failed"); }
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="master-editor">
        <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-lg font-display font-semibold">
              <Repeat2 size={18} className="text-blue-600" /> Master Schedule
            </div>
            <div className="text-sm text-slate-500">
              For each of the 14 days, list the shifts that need to be worked. Missing coverage shows up as Open shifts on the schedule.
            </div>
          </div>
          <div className="flex gap-2">
            {tab === "override" && overrideDays && (
              <Button variant="outline" onClick={clearOverride} data-testid="clear-override">
                <RotateCcw size={14} className="mr-1" /> Remove override
              </Button>
            )}
            <Button
              className="bg-blue-600 hover:bg-blue-700"
              onClick={save}
              disabled={loading || (tab === "override" && overrideDays === null)}
              data-testid="master-save"
            >
              <Save size={14} className="mr-1" /> Save
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="default" data-testid="tab-default">Default template</TabsTrigger>
            <TabsTrigger value="override" data-testid="tab-override">
              This pay period{payPeriodLabel ? ` · ${payPeriodLabel}` : ""}
              {overrideDays && (
                <span className="ml-2 text-[10px] uppercase tracking-widest text-blue-600">Override</span>
              )}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="default">
            <MasterGrid days={defaultDays} onAdd={addTo} onRemove={removeAt} workCodes={workCodes} codes={codes} />
          </TabsContent>
          <TabsContent value="override">
            {overrideDays === null ? (
              <div className="rounded-xl border border-dashed border-slate-300 p-8 text-center bg-slate-50">
                <div className="text-sm text-slate-700 mb-3">
                  This pay period is using the default template.
                </div>
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={startOverride} data-testid="start-override">
                  Create override from default
                </Button>
              </div>
            ) : (
              <MasterGrid days={overrideDays} onAdd={addTo} onRemove={removeAt} workCodes={workCodes} codes={codes} />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </TooltipProvider>
  );
}

function MasterGrid({ days, onAdd, onRemove, workCodes, codes }) {
  return (
    <div className="grid grid-cols-7 gap-2 mt-3">
      {days.map((row, i) => (
        <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-2" data-testid={`master-day-${i}`}>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">
            Day {i + 1} · {DAY_LABELS[i % 7]}
          </div>
          <div className="flex flex-wrap gap-1 min-h-[80px]">
            {row.map((code, j) => {
              const s = codes.find(c => c.code === code);
              const clr = colorForShift(s);
              return (
                <Tooltip key={j}>
                  <TooltipTrigger asChild>
                    <span
                      className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold ${clr.bg} ${clr.text} ${clr.border}`}
                      data-testid={`master-chip-${i}-${j}`}
                    >
                      {code}
                      <button onClick={() => onRemove(i, j)} className="hover:text-red-600" aria-label="Remove">
                        <X size={10} />
                      </button>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="text-xs">{s ? shiftLabel(s) : code}</TooltipContent>
                </Tooltip>
              );
            })}
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <button
                className="mt-1 w-full text-xs text-blue-600 hover:text-blue-800 flex items-center justify-center gap-1 py-1 rounded-md border border-dashed border-slate-300 hover:border-blue-400 transition-colors"
                data-testid={`master-add-${i}`}
              >
                <Plus size={12} /> Add
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-56 p-2" side="bottom" align="start">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Pick a shift</div>
              <div className="grid grid-cols-3 gap-1">
                {workCodes.map(c => {
                  const clr = colorForShift(c);
                  return (
                    <Tooltip key={c.code}>
                      <TooltipTrigger asChild>
                        <button
                          onClick={() => onAdd(i, c.code)}
                          className={`rounded-md border p-1.5 text-sm font-semibold ${clr.bg} ${clr.text} ${clr.border} hover:ring-2 ring-blue-500 ring-offset-1`}
                          data-testid={`master-pick-${i}-${c.code}`}
                        >
                          {c.code}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-xs">{shiftLabel(c)}</TooltipContent>
                    </Tooltip>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      ))}
    </div>
  );
}
